import { useMemo, useReducer } from 'react';
import type { IBenefitPlanFilterState, IBenefitPlan } from '@/@types/claims';

type FilterAction = // LOCAL_TYPE_JUSTIFIED: reducer action types used only in this hook
  | { type: 'SET_SEARCH'; query: string }
  | { type: 'SET_STATUS_FILTER'; status: string }
  | { type: 'SET_PROVIDER_FILTER'; providerId: string }
  | { type: 'SET_TYPE_FILTER'; insuranceTypeId: string }
  | { type: 'RESET' };

const initialFilterState: IBenefitPlanFilterState = {
  search: '',
  status: 'all',
  providerId: 'all',
  insuranceTypeId: 'all',
};

function filterReducer(
  state: IBenefitPlanFilterState,
  action: FilterAction
): IBenefitPlanFilterState {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, search: action.query };
    case 'SET_STATUS_FILTER':
      return {
        ...state,
        status: action.status as IBenefitPlanFilterState['status'], // AS_JUSTIFIED: status string from Select onValueChange narrowed to typed union
      };
    case 'SET_PROVIDER_FILTER':
      return { ...state, providerId: action.providerId };
    case 'SET_TYPE_FILTER':
      return { ...state, insuranceTypeId: action.insuranceTypeId };
    case 'RESET':
      return initialFilterState;
  }
}

export function useBenefitPlanFilters(plans: IBenefitPlan[]) {
  const [filters, dispatchFilter] = useReducer(filterReducer, initialFilterState);

  const filteredPlans = useMemo(() => {
    let result = [...plans];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q));
    }

    if (filters.status !== 'all') {
      result = result.filter(p => p.status === filters.status);
    }

    if (filters.providerId !== 'all') {
      result = result.filter(p => p.providerId === filters.providerId);
    }

    if (filters.insuranceTypeId !== 'all') {
      result = result.filter(p => p.insuranceTypeId === filters.insuranceTypeId);
    }

    result.sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }, [plans, filters.search, filters.status, filters.providerId, filters.insuranceTypeId]);

  return { filters, filteredPlans, dispatchFilter };
}
